import { Panel, Tab, Table, TableRowExpansionEvent, TableSelectionEvent } from "@mcleod/components";
import { AutogenLayoutMiscInvoiceProcessing } from "./autogen/AutogenLayoutMiscInvoiceProcessing";
import { ImageTable } from "@mcleod/common";
import { ArrayUtil, LogManager, Model, ModelRow } from "@mcleod/core";
import { AutogenLayoutMiscInvoiceExpand } from "./autogen/AutogenLayoutMiscInvoiceExpand";
import { MiscInvoiceDetailTable } from "./MiscInvoiceDetailTable";
import { MiscInvoiceProcessingPending } from "./MiscInvoiceProcessingPending";
import { MiscInvoiceProcessingReady } from "./MiscInvoiceProcessingReady";

const log = LogManager.getLogger("lme.ar.MiscInvoiceProcessing");

export interface MiscInvoiceProcessingLayout {
    tableMiscInvoice: Table;
    type: MiscInvoiceProcessingTableType;
}

export enum MiscInvoiceProcessingTableType {
    PENDING_REVIEW = "PENDING_REVIEW",
    READY_TO_PROCESS = "READY_TO_PROCESS"
}

export class MiscInvoiceProcessing extends AutogenLayoutMiscInvoiceProcessing {
    private segAllocControl: ModelRow;

    override onLoad(): void | Promise<void> {
        this.setupNestedLayout(this.layoutPending);
        this.setupNestedLayout(this.layoutReady);
        this.searchChildren();
        Model.searchSingleRecord("lme/settlement/seg-alloc-control", null, { extraFields: "lme/dispatch/seg-alloc-control-single" })
            .then(response => {
                this.segAllocControl = response;
            })
            .catch(error => {
                log.error("Failed to load segment allocation control settings:", error);
                this.segAllocControl = null;
            });
    }

    private setupNestedLayout(layout: MiscInvoiceProcessingPending | MiscInvoiceProcessingReady) {
        layout.type = layout instanceof MiscInvoiceProcessingPending ? MiscInvoiceProcessingTableType.PENDING_REVIEW : MiscInvoiceProcessingTableType.READY_TO_PROCESS;
        layout.layoutMiscInvoiceProcessingToolPanel.searchCallback = this.refreshTabs.bind(this);
        layout.refreshTabsCallback = () => this.refreshTabs();

        const table = layout.tableMiscInvoice;
        table.addRowExpandListener(this.commonOnRowExpand.bind(this));
        table.doOnResolveRowEdit = this.commonDoOnResolveRowEdit();
        table.addDataDisplayListener(() => this.syncTabCounts());
        // Because DeleteButton.handleDelete() doesn't fire a TableContentsChangedEvent, we need to sync the tab counts when the selection changes
        // Deleting a row from the header will fire a TableSelectionEvent with an empty newRows array, CWG will clean this up in the future
        table.addSelectionListener((event: TableSelectionEvent) => {
            if (ArrayUtil.isEmptyArray(event.newRows)) {
                this.syncTabCounts();
            }
        });
    }

    refreshTabs() {
        this.searchChildren();
    }

    async searchChildren() {
        await Promise.all([
            this.layoutPending.mainDataSource.search(),
            this.layoutReady.mainDataSource.search()
        ]);
    }

    private syncTabCounts() {
        this.updateTabTitle(this.tabPendingReview, "Pending Review", this.layoutPending.tableMiscInvoice.rowCount);
        this.updateTabTitle(this.tabReadyToProcess, "Ready to Process", this.layoutReady.tableMiscInvoice.rowCount);
    }

    updateTabTitle(tab: Tab, baseCaption: string, count: number) {
        tab.caption = `${baseCaption} (${count?? 0})`;
    }

    // START - Common Handlers for both tables
    commonOnRowExpand(event: TableRowExpansionEvent) {
        this.initializeDetailTab(event);
        this.initializeImagesTab(event);
    }

    initializeDetailTab(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const expandPanel = event.expandComponentParent as Panel;
        const layoutMiscInvoiceExpand = expandPanel.findComponentById("layoutMiscInvoiceExpand") as AutogenLayoutMiscInvoiceExpand;
        const layoutMiscInvoiceDetailTable = layoutMiscInvoiceExpand.layoutMiscInvoiceDetail as MiscInvoiceDetailTable;
        layoutMiscInvoiceDetailTable.tableMiscInvoiceDetail.allowEdit = false;
        layoutMiscInvoiceDetailTable.tableMiscInvoiceDetail.allowDelete = false;
        const miscInvoiceId = modelRow.getString("id", null);
        if (miscInvoiceId) {
            layoutMiscInvoiceExpand.mainDataSource.search({ "id": miscInvoiceId })
                .catch(error => {
                    log.error(`Failed to load expanded row data for invoice ${miscInvoiceId}:`, error);
                });
        }

        layoutMiscInvoiceExpand.textboxAllocationCode.visible = !!this.segAllocControl && this.segAllocControl.getBoolean("is_active", false);
    }

    initializeImagesTab(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const expandPanel = event.expandComponentParent as Panel;
        const imageTable = expandPanel.findComponentById("layoutImageTable") as ImageTable;
        const id = modelRow?.getString("id", null);
        if (id && imageTable) {
            imageTable.buttonUploadImage.visible = false;
            imageTable.populateForRow("MISC_BILL", id);
        }
    }

    commonDoOnResolveRowEdit() {
        return async (updatedRow: ModelRow) => {
            if (updatedRow.hasChanged()) {
                await this.searchChildren();
                return null;
            }
            return updatedRow;
        }
    }
    // END - Common Handlers for both tables
}
